import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  MLB_MY_CLUB_SLUG,
  MLB_MY_CLUB_SLUG_CARDS,
  MLB_MY_CLUB_SLUG_CHALLENGES,
  MLB_MY_CLUB_SLUG_COLLECTIONS,
  MLB_MY_CLUB_SLUG_COLLECTIONS_COLLECTIONSLUG,
  MLB_MY_CLUB_SLUG_HISTORY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { navLabels } from '@sorare/core/src/lib/glossary';

import DeckEditorDialog from '@sorare/marketplace/src/components/decks/DeckEditorDialog';

import { CollectionPreview } from '@sorare/gameplay/src/components/collections/CollectionPreview';

import { Aside } from '@sorare/manager/src/components/profile/Aside';
import { Challenges } from '@sorare/manager/src/components/profile/Challenges';
import { ClubLayout } from '@sorare/manager/src/components/profile/ClubLayout';
import { Collections } from '@sorare/manager/src/components/profile/Collections';
import { Gallery } from '@sorare/manager/src/components/profile/Gallery';
import { computeCardsToDisplay } from '@sorare/manager/src/components/profile/Gallery/utils';
import { ManagerIDHeader } from '@sorare/manager/src/components/profile/ManagerIDHeader';
import { OtherClubs } from '@sorare/manager/src/components/profile/OtherClubs';
import { Pro } from '@sorare/manager/src/components/profile/Pro';
import { ReferYourFriends } from '@sorare/manager/src/components/profile/ReferYourFriends';
import {
  FlexGrowBlock,
  Grid,
  Main,
  NewGrid,
} from '@sorare/manager/src/components/profile/ui';

import {
  BaseballMyClubQuery,
  BaseballMyClubQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';
import collectionsBaseball from './assets/collections-baseball.png';

const BASEBALL_MY_CLUB_QUERY = gql`
  query BaseballMyClubQuery($user: String!) {
    user(slug: $user) {
      slug
      id
      baseballUserProfile {
        id
        bannerUrl
        clubName
        shieldUrl
        socialPictureFingerprint
        managerProfileSocialPictureUrls {
          ...Gallery_socialPictureDerivative
        }
        gallerySocialPictureUrls {
          ...Gallery_socialPictureDerivative
        }
        highlightedDeck {
          slug
          name
          socialPictureUrls {
            ...Gallery_socialPictureDerivative
          }
          ...Gallery_deck
        }
        ...ManagerIDHeader_userSportProfile
      }
      baseballSportProfile: anyUserSportProfile(sport: BASEBALL) {
        id
        cardsCount {
          ...Gallery_cardCount
        }
      }
      baseballTrophiesSummary {
        ...Pro_trophiesSummary
      }
      baseballCards: cards(first: 10, sport: BASEBALL) {
        nodes {
          slug
          ...ComputeCardsToDisplay_anyCard
          ...Gallery_anyCard
        }
      }
      blockchainBaseballCards: cards(
        first: 10
        sport: BASEBALL
        rarities: [limited, rare, super_rare, unique]
      ) {
        nodes {
          slug
          ...ComputeCardsToDisplay_anyCard
          ...Gallery_anyCard
        }
      }
      cardCollections(startedOnly: true, first: 10, sport: BASEBALL) {
        nodes {
          slug
          userCardCollection(forUserSlug: $user) {
            id
          }
          ...CollectionPreview_cardCollection
        }
        totalCount
      }
      managerProgression(sport: BASEBALL) {
        id
        ...ManagerIDHeader_managerProgression
        ...Challenges_ManagerProgression
      }
      ...OtherClubs_user
      ...ManagerIDHeader_user
    }
  }
  ${OtherClubs.fragments.user}
  ${Pro.fragments.trophiesSummary}
  ${Gallery.fragments.cardCount}
  ${Gallery.fragments.deck}
  ${Gallery.fragments.socialPictureDerivative}
  ${Gallery.fragments.anyCard}
  ${CollectionPreview.fragments.cardCollection}
  ${Challenges.fragments.managerProgression}
  ${ManagerIDHeader.fragments.user}
  ${ManagerIDHeader.fragments.userSportProfile}
  ${ManagerIDHeader.fragments.managerProgression}
  ${computeCardsToDisplay.fragments.anyCard}
` as TypedDocumentNode<BaseballMyClubQuery, BaseballMyClubQueryVariables>;

const MyClub = () => {
  const { slug } = useTypedParams<PageParams>();
  const {
    flags: { enableMyClubSplit = false },
  } = useFeatureFlags();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { currentUser } = useCurrentUserContext();
  const { formatMessage } = useIntl();
  const isCurrentUserPage = slug === currentUser?.slug;
  const { data, loading } = useQuery(BASEBALL_MY_CLUB_QUERY, {
    variables: { user: slug },
  });
  const [editingListSlug, setEditingListSlug] = useState<string | null>();

  if (loading || !data?.user) {
    return <LoadingIndicator fullHeight />;
  }

  const pageHeader = !enableMyClubSplit && (
    <PageHeader
      text={
        isCurrentUserPage
          ? navLabels.myClub
          : formatMessage(navLabels.userClub, {
              username: data.user.nickname,
            })
      }
    />
  );

  const cards = computeCardsToDisplay(
    data.user.baseballCards.nodes,
    data.user.blockchainBaseballCards.nodes
  );
  const shareProps = {
    managerProfilePath: generatePath(MLB_MY_CLUB_SLUG, { slug }),
    managerProfileSocialPictures:
      data?.user.baseballUserProfile?.managerProfileSocialPictureUrls,
    galleryPath: generatePath(MLB_MY_CLUB_SLUG_CARDS, { slug }),
    gallerySocialPictures:
      data?.user.baseballUserProfile?.gallerySocialPictureUrls,
    highlightedDeckPath: `${generatePath(MLB_MY_CLUB_SLUG_CARDS, {
      slug,
    })}?${buildFilterQuery({
      [SEARCH_PARAMS.DECK]:
        data?.user.baseballUserProfile?.highlightedDeck?.name,
    })}`,
    highlightedDeckSocialPictures:
      data?.user.baseballUserProfile?.highlightedDeck?.socialPictureUrls,
  };

  const blocks = {
    header: (
      <ManagerIDHeader
        user={data.user}
        sport={Sport.BASEBALL}
        userSportProfile={data.user.baseballUserProfile}
        managerProgression={data.user.managerProgression}
        {...shareProps}
      />
    ),
    pro: (
      <Pro
        trophiesSummary={data?.user.baseballTrophiesSummary}
        historyUrl={generatePath(MLB_MY_CLUB_SLUG_HISTORY, { slug })}
      />
    ),
    gallery: (
      <>
        <Gallery
          cards={cards}
          sport={Sport.BASEBALL}
          onAddDeck={() => setEditingListSlug(null)}
          onEditDeck={deckSlug => setEditingListSlug(deckSlug)}
          cardCount={data.user.baseballSportProfile?.cardsCount}
          {...shareProps}
        />
        {editingListSlug !== undefined && (
          <DeckEditorDialog
            sport={Sport.BASEBALL}
            slug={editingListSlug || ''}
            defaultName="Hall Of Fame"
            onClose={() => setEditingListSlug(undefined)}
          />
        )}
      </>
    ),
    collections: (
      <Collections
        backgroundImage={collectionsBaseball}
        collectionsUrl={generatePath(MLB_MY_CLUB_SLUG_COLLECTIONS, { slug })}
        totalCollections={data.user.cardCollections?.totalCount || 0}
        collections={data.user.cardCollections?.nodes || []}
        renderCollection={collection => (
          <CollectionPreview
            collection={collection}
            to={generatePath(MLB_MY_CLUB_SLUG_COLLECTIONS_COLLECTIONSLUG, {
              slug,
              collectionSlug: collection.slug,
            })}
          />
        )}
      />
    ),
    challenges: data.user.managerProgression && (
      <Challenges
        challengesUrl={generatePath(MLB_MY_CLUB_SLUG_CHALLENGES, { slug })}
        managerProgression={data.user.managerProgression}
        showUnclaimedBadge={isCurrentUserPage}
      />
    ),
    otherClubs: <OtherClubs user={data?.user} />,
    refer: <ReferYourFriends />,
  };

  if (enableMyClubSplit) {
    if (!isLaptopAndAbove) {
      return (
        <ClubLayout home bannerUrl={data?.user.baseballUserProfile?.bannerUrl}>
          <Grid>
            {pageHeader}
            {blocks.header}
            {blocks.gallery}
          </Grid>
        </ClubLayout>
      );
    }

    return (
      <ClubLayout home bannerUrl={data?.user.baseballUserProfile?.bannerUrl}>
        <NewGrid>
          {pageHeader}
          <Main>
            <FlexGrowBlock>{blocks.header}</FlexGrowBlock>
          </Main>
          <Aside>
            <FlexGrowBlock>{blocks.gallery}</FlexGrowBlock>
          </Aside>
        </NewGrid>
      </ClubLayout>
    );
  }

  if (!isLaptopAndAbove) {
    return (
      <ClubLayout home bannerUrl={data?.user.baseballUserProfile?.bannerUrl}>
        <Grid>
          {pageHeader}
          {blocks.header}
          {blocks.gallery}
          {blocks.pro}
          {blocks.challenges}
          {blocks.collections}
          {blocks.otherClubs}
          {blocks.refer}
        </Grid>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout home bannerUrl={data?.user.baseballUserProfile?.bannerUrl}>
      <Grid>
        {pageHeader}
        <Main>
          {blocks.header}
          {blocks.gallery}
          {blocks.collections}
        </Main>
        <Aside>
          {blocks.pro}
          {blocks.challenges}
          {blocks.otherClubs}
          {blocks.refer}
        </Aside>
      </Grid>
    </ClubLayout>
  );
};

export default MyClub;
